/**
 * @fileoverview Event Emitter Base Class
 * Provides event system functionality for cart components
 * 
 * 📡 EVENT SUMMARY:
 * 
 * BASE EVENTS:
 * - listener:added    → Emitted when a listener is added
 * - listener:removed  → Emitted when a listener is removed
 * - listener:error    → Emitted when a listener throws an error
 * 
 * METHODS:
 * - on(event, listener)     → Add event listener
 * - once(event, listener)   → Add one-time event listener
 * - off(event, listener)    → Remove event listener
 * - emit(event, data)       → Emit event to all listeners
 * - removeAllListeners()    → Remove all listeners
 * 
 * EVENT DATA STRUCTURE:
 * - listener:added: { event: string, listener: Function }
 * - listener:removed: { event: string, listener: Function }
 * - listener:error: { event: string, error: Error, listener: Function }
 */

import { Debug } from '../utils/index.js';

/**
 * Event Emitter class
 * @class
 */
export class EventEmitter {
  constructor() {
    this.events = new Map();
    this.maxListeners = 10;
    this.emitting = new Set(); // Track events being emitted
  }

  /**
   * Add event listener
   * @param {string} eventName - Event name
   * @param {Function} listener - Event listener function
   * @returns {EventEmitter} This instance for chaining
   */
  on(eventName, listener) {
    if (typeof listener !== 'function') {
      throw new Error('Listener must be a function');
    }

    if (!this.events.has(eventName)) {
      this.events.set(eventName, new Set());
    }

    const listeners = this.events.get(eventName);
    
    // Warn if too many listeners
    if (listeners.size >= this.maxListeners) {
      Debug.log('warn', `Possible memory leak: ${listeners.size} listeners for event "${eventName}"`);
    }

    listeners.add(listener);
    return this;
  }

  /**
   * Add one-time event listener
   * @param {string} eventName - Event name
   * @param {Function} listener - Event listener function
   * @returns {EventEmitter} This instance for chaining
   */
  once(eventName, listener) {
    const onceWrapper = (...args) => {
      this.off(eventName, onceWrapper);
      listener.apply(this, args);
    };
    
    return this.on(eventName, onceWrapper);
  }

  /**
   * Remove event listener
   * @param {string} eventName - Event name
   * @param {Function} listener - Event listener function
   * @returns {EventEmitter} This instance for chaining
   */
  off(eventName, listener) {
    const listeners = this.events.get(eventName);
    if (listeners) {
      listeners.delete(listener);
      
      // Clean up empty event sets
      if (listeners.size === 0) {
        this.events.delete(eventName);
      }
    }
    return this;
  }

  /**
   * Remove all listeners for an event
   * @param {string} [eventName] - Event name (if not provided, removes all listeners)
   * @returns {EventEmitter} This instance for chaining
   */
  removeAllListeners(eventName) {
    if (eventName) {
      this.events.delete(eventName);
    } else {
      this.events.clear();
    }
    return this;
  }

  /**
   * Emit event to all listeners
   * @param {string} eventName - Event name
   * @param {...any} args - Arguments to pass to listeners
   * @returns {boolean} True if event had listeners
   */
  emit(eventName, ...args) {
    // Prevent recursive emission of the same event
    const eventKey = `${eventName}_${JSON.stringify(args)}`;
    if (this.emitting.has(eventKey)) {
      Debug.log('warn', `Preventing recursive emission of event "${eventName}"`, {
        args,
        stackTrace: new Error().stack
      });
      return false;
    }

    const listeners = this.events.get(eventName);
    if (!listeners || listeners.size === 0) {
      return false;
    }

    // Track event emission
    this.emitting.add(eventKey);

    try {
      // Also dispatch DOM event for external listeners
      this.dispatchDOMEvent(eventName, args[0]);

      // Call all listeners
      for (const listener of listeners) {
        try {
          listener.apply(this, args);
        } catch (error) {
          Debug.log('error', `Error in event listener for "${eventName}"`, error);
        }
      }
    } finally {
      // Clean up tracking
      this.emitting.delete(eventKey);
    }

    return true;
  }

  /**
   * Dispatch corresponding DOM event
   * @private
   * @param {string} eventName - Event name
   * @param {any} detail - Event detail
   */
  dispatchDOMEvent(eventName, detail) {
    try {
      if (eventName.startsWith('cart:')) {
        console.log('[EventEmitter] dispatchDOMEvent', eventName, { item_count: detail?.cart?.item_count });
      }
      const domEvent = new CustomEvent(eventName, {
        detail,
        bubbles: true,
        cancelable: true
      });
      document.dispatchEvent(domEvent);
    } catch (error) {
      Debug.log('error', `Failed to dispatch DOM event "${eventName}"`, error);
    }
  }

  /**
   * Get listener count for event
   * @param {string} eventName - Event name
   * @returns {number} Number of listeners
   */
  listenerCount(eventName) {
    const listeners = this.events.get(eventName);
    return listeners ? listeners.size : 0;
  }

  /**
   * Get all event names
   * @returns {string[]} Array of event names
   */
  eventNames() {
    return Array.from(this.events.keys());
  }

  /**
   * Set maximum number of listeners per event
   * @param {number} max - Maximum listeners
   * @returns {EventEmitter} This instance for chaining
   */
  setMaxListeners(max) {
    this.maxListeners = max;
    return this;
  }

  /**
   * Get maximum number of listeners per event
   * @returns {number} Maximum listeners
   */
  getMaxListeners() {
    return this.maxListeners;
  }
} 